import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Form, Input, Select, Space } from "antd";
import { useTranslation } from "react-i18next";

export interface FilterField {
  /** Has to match the query parameter the controller reads. */
  name: string;
  label: string;
  /**
   * Turns the field into a picker. The values the server understands, so the
   * list belongs to whatever endpoint reports them — a filter must not invent
   * its own codes.
   */
  options?: { value: string | number; label: string }[];
}

export interface FilterValues {
  [name: string]: string;
}

/**
 * The filters are applied on submit rather than on every keystroke: the server
 * matches them as a prefix over the whole table, so a request per character
 * would be both slow and pointless.
 */
export function FilterBar({
  fields,
  onApply,
  loading,
}: {
  fields: FilterField[];
  onApply: (values: FilterValues) => void;
  loading?: boolean;
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm<FilterValues>();

  const apply = (values: FilterValues) => {
    const cleaned: FilterValues = {};

    Object.entries(values).forEach(([name, value]) => {
      // A picker hands back whatever type its options carry, a text field a
      // string — everything ends up in the query string either way.
      const trimmed = value === undefined || value === null ? "" : String(value).trim();
      if (trimmed) cleaned[name] = trimmed;
    });

    onApply(cleaned);
  };

  return (
    <Form
      form={form}
      layout="inline"
      onFinish={apply}
      style={{ marginBottom: 16, rowGap: 8 }}
    >
      {fields.map((field) => (
        <Form.Item key={field.name} name={field.name} label={field.label}>
          {field.options ? (
            <Select allowClear options={field.options} style={{ width: 170 }} />
          ) : (
            <Input allowClear style={{ width: 170 }} />
          )}
        </Form.Item>
      ))}

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={loading}>
            {t("filter.search")}
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              form.resetFields();
              onApply({});
            }}
          >
            {t("app.filter_reset")}
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
}
